<script setup>
import { usePlatformApi } from '@/utils/platformApi'

// Sama persis pola auth-check halaman /platform lain (tenants/registrations
// index.vue) — lihat komentar di sana untuk penjelasan lengkap.
definePage({ meta: { layout: 'platform', public: true } })

const router = useRouter()

const platformAccessToken = useCookie('platformAccessToken')
const platformAdminData = useCookie('platformAdminData')

if (!platformAccessToken.value)
  await router.push('/platform/login')

// ─── State ────────────────────────────────────────────────────────────────
const orders = ref([])
const loading = ref(false)
const page = ref(1)
const perPage = ref(20)
const totalItems = ref(0)
const sortBy = ref([{ key: 'created_at', order: 'desc' }])

// Filter
const search = ref('')
const filterStatus = ref(null)
const filterType = ref(null)
const dateFrom = ref('')
const dateTo = ref('')

const statusOptions = [
  { title: 'Semua Status', value: null },
  { title: 'Menunggu Bayar', value: 'pending' },
  { title: 'Sudah Bayar', value: 'paid' },
  { title: 'Kedaluwarsa', value: 'expired' },
  { title: 'Gagal', value: 'failed' },
]

const typeOptions = [
  { title: 'Semua Tipe', value: null },
  { title: 'Registrasi Baru', value: 'registration' },
  { title: 'Perpanjangan', value: 'renewal' },
]

const statusMeta = {
  pending: { color: 'warning', label: 'Menunggu Bayar' },
  paid: { color: 'success', label: 'Sudah Bayar' },
  expired: { color: 'error', label: 'Kedaluwarsa' },
  failed: { color: 'error', label: 'Gagal' },
}

const typeMeta = {
  registration: { color: 'info', label: 'Registrasi Baru' },
  renewal: { color: 'secondary', label: 'Perpanjangan' },
}

const headers = [
  { title: 'Tenant / Pendaftar', key: 'tenant', sortable: false },
  { title: 'Paket', key: 'plan', sortable: false },
  { title: 'Tipe', key: 'type', sortable: false },
  { title: 'Periode', key: 'billing_period', sortable: false },
  { title: 'Bulan', key: 'months', sortable: false, align: 'center' },
  { title: 'Jumlah Bayar', key: 'amount', sortable: true, align: 'end' },
  { title: 'Status', key: 'status', sortable: true },
  { title: 'Tanggal Bayar', key: 'paid_at', sortable: true },
  { title: 'Berlaku Sampai', key: 'expires_at', sortable: false },
  { title: 'Aksi', key: 'actions', sortable: false, align: 'center' },
]

const formatRupiah = val =>
  new Intl.NumberFormat('id-ID', { style: 'currency', currency: 'IDR', minimumFractionDigits: 0 }).format(val ?? 0)

const formatDate = iso => (iso ? new Date(iso).toLocaleDateString('id-ID', { day: '2-digit', month: 'short', year: 'numeric' }) : '-')

const formatDateTime = iso => (iso
  ? new Date(iso).toLocaleString('id-ID', { day: '2-digit', month: 'short', year: 'numeric', hour: '2-digit', minute: '2-digit' })
  : null)

// ─── Aksi: kirim invoice via email / WhatsApp ──────────────────────────────
// Email masuk akal untuk order yang masih menunggu pembayaran (butuh link
// invoice yang belum kedaluwarsa) MAUPUN yang sudah lunas (kirim ulang bukti
// bayar, lihat InvoiceLinkMail -- isinya menyesuaikan status order). WhatsApp
// cuma untuk yang masih pending -- mengirim wa.me berisi "bukti sudah lunas"
// kurang masuk akal dibanding email sebagai bukti resmi.
const canEmailInvoice = item => (item.status === 'pending' && !!item.xendit_invoice_url) || item.status === 'paid'
const canWhatsapp = item => item.status === 'pending' && !!item.xendit_invoice_url

const recipientOf = item => item.tenant ?? item.customer_registration ?? null

const resendingId = ref(null)
const snackbar = ref({ show: false, text: '', color: 'success' })

const showSnackbar = (text, color = 'success') => {
  snackbar.value = { show: true, text, color }
}

const resendInvoiceEmail = async item => {
  resendingId.value = item.id
  try {
    const res = await usePlatformApi(`/api/v1/platform/orders/${item.id}/resend-invoice-email`).post()
    const body = res?.data ?? res

    // Update langsung di tempat supaya status "Terkirim" di kolom Aksi
    // ter-update tanpa perlu fetchOrders() ulang. Bracket notation supaya
    // lolos aturan lint camelcase (nama field ini API contract, snake_case).
    item['invoice_email_sent_at'] = body?.data?.['invoice_email_sent_at'] ?? new Date().toISOString()

    showSnackbar(body?.message ?? 'Invoice berhasil dikirim ulang.')
  } catch (err) {
    if (await handleAuthError(err)) return

    showSnackbar(err?.data?.message ?? err?.response?.data?.message ?? 'Gagal mengirim ulang invoice.', 'error')
  } finally {
    resendingId.value = null
  }
}

// Nomor disimpan lokal (mis. "08123456789", tanpa kode negara) -- ubah ke
// format wa.me (62...) di sisi client, tidak ada helper normalisasi di
// backend untuk ini.
//
// Isi pesan sengaja dibuat semirip mungkin dengan emails/invoice-link.blade.php
// (nama paket + periode + jumlah tagihan + link) supaya konsisten dengan
// email, bukan cuma link telanjang.
const waLink = item => {
  const recipient = recipientOf(item)
  const phone = recipient?.phone?.replace(/\D/g, '')
  if (!phone) return null

  const normalized = phone.startsWith('0') ? `62${phone.slice(1)}` : phone
  const name = item.tenant?.name ?? item.customer_registration?.business_name ?? ''

  const message = [
    `Halo${name ? ' ' + name : ''},`,
    '',
    `Berikut tagihan Anda untuk paket ${item.plan?.name ?? '-'} (${item.billing_period_label ?? '-'}) yang masih menunggu pembayaran.`,
    '',
    `Jumlah Tagihan: ${formatRupiah(item.amount)}`,
    '',
    `Bayar Sekarang: ${item.xendit_invoice_url}`,
    '',
    'Terima kasih.',
  ].join('\n')

  return `https://wa.me/${normalized}?text=${encodeURIComponent(message)}`
}

// Sama seperti halaman /platform lain: token sudah tidak valid -> paksa login ulang.
const handleAuthError = async err => {
  const status = err?.status ?? err?.response?.status
  if (status === 401 || status === 403) {
    platformAccessToken.value = null
    platformAdminData.value = null
    await router.push('/platform/login')

    return true
  }

  return false
}

const fetchOrders = async () => {
  loading.value = true
  try {
    const sort = sortBy.value?.[0]

    const query = {
      page: page.value,
      per_page: perPage.value,
      sort_by: sort?.key ?? 'created_at',
      sort_dir: sort?.order ?? 'desc',
    }

    if (search.value) query.search = search.value
    if (filterStatus.value) query.status = filterStatus.value
    if (filterType.value) query.type = filterType.value
    if (dateFrom.value) query.date_from = dateFrom.value
    if (dateTo.value) query.date_to = dateTo.value

    const res = await usePlatformApi('/api/v1/platform/orders', { query }).get()
    const body = res?.data ?? res

    orders.value = body?.data ?? []
    totalItems.value = body?.pagination?.total ?? 0
  } catch (err) {
    if (await handleAuthError(err)) return

    console.error('[Platform] fetch orders error', err)
    orders.value = []
  } finally {
    loading.value = false
  }
}

watch([page, sortBy, search, filterStatus, filterType, dateFrom, dateTo], fetchOrders, { immediate: true, deep: true })
</script>

<template>
  <div class="pa-6">
    <div class="d-flex align-center justify-space-between mb-4 flex-wrap gap-3">
      <div>
        <h4 class="text-h4">
          Riwayat Orders / Langganan
        </h4>
        <p class="text-body-2 text-medium-emphasis mb-0">
          Login sebagai {{ platformAdminData?.name }}
        </p>
      </div>
    </div>

    <VCard>
      <VCardText class="d-flex flex-wrap gap-3 align-center">
        <VTextField
          v-model="search"
          label="Cari nama tenant/bisnis"
          density="compact"
          style="min-width: 220px"
          clearable
        />
        <VSelect
          v-model="filterStatus"
          :items="statusOptions"
          label="Status"
          density="compact"
          style="min-width: 170px"
        />
        <VSelect
          v-model="filterType"
          :items="typeOptions"
          label="Tipe"
          density="compact"
          style="min-width: 170px"
        />
        <VTextField
          v-model="dateFrom"
          label="Dari Tanggal"
          type="date"
          density="compact"
          style="min-width: 160px"
        />
        <VTextField
          v-model="dateTo"
          label="Sampai Tanggal"
          type="date"
          density="compact"
          style="min-width: 160px"
        />
      </VCardText>

      <VDivider />

      <VDataTableServer
        v-model:page="page"
        v-model:sort-by="sortBy"
        :headers="headers"
        :items="orders"
        :items-length="totalItems"
        :loading="loading"
        :items-per-page="perPage"
        no-data-text="Belum ada order/langganan."
      >
        <template #item.tenant="{ item }">
          <div v-if="item.tenant">
            <div class="font-weight-medium">
              {{ item.tenant.name }}
            </div>
            <div class="text-caption text-medium-emphasis">
              {{ item.tenant.slug }}
            </div>
          </div>
          <div v-else-if="item.customer_registration">
            <div class="font-weight-medium">
              {{ item.customer_registration.business_name }}
            </div>
            <div class="text-caption text-medium-emphasis">
              belum jadi tenant
            </div>
          </div>
          <span
            v-else
            class="text-medium-emphasis"
          >-</span>
        </template>

        <template #item.plan="{ item }">
          {{ item.plan?.name ?? '-' }}
        </template>

        <template #item.type="{ item }">
          <VChip
            :color="typeMeta[item.type]?.color ?? 'default'"
            size="small"
          >
            {{ typeMeta[item.type]?.label ?? item.type }}
          </VChip>
        </template>

        <template #item.billing_period="{ item }">
          {{ item.billing_period_label }}
        </template>

        <template #item.amount="{ item }">
          {{ formatRupiah(item.amount) }}
        </template>

        <template #item.status="{ item }">
          <VChip
            :color="statusMeta[item.status]?.color ?? 'default'"
            size="small"
          >
            {{ statusMeta[item.status]?.label ?? item.status }}
          </VChip>
        </template>

        <template #item.paid_at="{ item }">
          {{ formatDate(item.paid_at) }}
        </template>

        <template #item.expires_at="{ item }">
          {{ formatDate(item.tenant?.expires_at) }}
        </template>

        <template #item.actions="{ item }">
          <div
            v-if="canEmailInvoice(item) || canWhatsapp(item)"
            class="d-flex flex-column align-center gap-1"
          >
            <div class="d-flex gap-1 justify-center">
              <VBtn
                v-if="canEmailInvoice(item)"
                icon
                size="small"
                variant="text"
                color="primary"
                :loading="resendingId === item.id"
                :disabled="!recipientOf(item)?.email"
                @click="resendInvoiceEmail(item)"
              >
                <VIcon icon="ri-mail-send-line" />
                <VTooltip activator="parent">
                  {{ recipientOf(item)?.email ? 'Kirim Invoice via Email' : 'Tidak ada email' }}
                </VTooltip>
              </VBtn>

              <VBtn
                v-if="canWhatsapp(item)"
                icon
                size="small"
                variant="text"
                color="success"
                :disabled="!waLink(item)"
                :href="waLink(item) ?? undefined"
                target="_blank"
                rel="noopener noreferrer"
              >
                <VIcon icon="ri-whatsapp-line" />
                <VTooltip activator="parent">
                  {{ waLink(item) ? 'Kirim Invoice via WhatsApp' : 'Tidak ada nomor HP' }}
                </VTooltip>
              </VBtn>
            </div>

            <span
              class="text-caption"
              :class="item.invoice_email_sent_at ? 'text-success' : 'text-medium-emphasis'"
            >
              {{ item.invoice_email_sent_at ? `Terkirim ${formatDateTime(item.invoice_email_sent_at)}` : 'Belum terkirim' }}
            </span>
          </div>
          <span
            v-else
            class="text-caption text-medium-emphasis"
          >-</span>
        </template>
      </VDataTableServer>
    </VCard>

    <VSnackbar
      v-model="snackbar.show"
      :color="snackbar.color"
      timeout="4000"
      location="top"
    >
      {{ snackbar.text }}
    </VSnackbar>
  </div>
</template>